import {
  Box,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  TabsWithContent,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  CreateTemplate,
  isConflictError,
  templatesApi,
  TemplateTypes,
} from 'apis/templates';
import { FieldTemplate } from 'components/fieldTemplate';
import { HookFormSwitch, HookFormText } from 'components/hookForm';
// eslint-disable-next-line no-restricted-imports
import {
  defaultLanguageCode,
  helpText,
  languages,
} from 'components/shared/templatesModal/utils';
import { validateName } from 'utils/formValidator';

import {
  NotificationType,
  useNotifications,
} from '/assets/hooks/useNotifications';

import { TEMPLATE_NAMES } from '../../../../../../settingsTemplates.view';
import styles from '../styles/templateModal.module.scss';

type SettingsTemplatesFormValues = CreateTemplate;

type CreateTemplateModalProps = {
  templateType: TemplateTypes;
  isModalOpen: boolean;
  onClose: () => void;
  onCreate?: () => void;
};

export const CreateTemplateModal = ({
  templateType,
  isModalOpen,
  onClose,
  onCreate,
}: CreateTemplateModalProps): JSX.Element => {
  const notify = useNotifications();

  const { handleSubmit, control, formState, watch } =
    useForm<SettingsTemplatesFormValues>({
      defaultValues: {
        content: '',
        name: '',
        subject: '',
        type: templateType,
        translations: languages.reduce((currentTranslations, { code }) => {
          currentTranslations[code] = {
            subject: '',
            content: '',
            isEnabled: false,
          };
          return currentTranslations;
        }, {}),
      },
      mode: 'onBlur',
    });

  const defaultLanguageContent = watch('content');

  const isOpen = useMemo(() => isModalOpen, [isModalOpen]);

  const tabsLabels = useMemo(
    () =>
      languages.map((lang) => ({
        id: lang.code,
        label: (
          <>
            <span className={styles.tabLanguageName}>{lang.label}</span>
            {lang.code === defaultLanguageCode ? (
              <span className={styles.tabLanguageDefault}>Default</span>
            ) : (
              <HookFormSwitch
                control={control}
                name={`translations.${lang.code}.isEnabled`}
              />
            )}
          </>
        ),
      })),
    [],
  );

  const [createTemplate] = templatesApi.useCreateTemplateMutation();
  const [updateTemplateTranslations] =
    templatesApi.useUpdateTemplateTranslationsMutation();

  const onSubmit = useCallback(
    async (formData: SettingsTemplatesFormValues) => {
      const { translations, ...rest } = formData;

      try {
        const {
          data: { id, name },
        } = await createTemplate({
          ...rest,
        }).unwrap();

        ampli.settingsTemplatesModalFormSubmitted({
          mode: 'add',
          templateType,
          templateName: name,
        });

        const shouldUpdateTemplateTranslations = Object.values(
          translations || [],
        ).some((template) => {
          const { subject, content, isEnabled } = template;
          const hasSubject = subject && subject.length > 0;
          const hasContent = content && content.length > 0;

          return hasSubject || hasContent || isEnabled;
        });

        if (shouldUpdateTemplateTranslations) {
          await updateTemplateTranslations({
            id,
            translations: translations || {},
          }).unwrap();
        }

        onCreate?.();

        notify(
          {
            title: 'Template created',
            text: `Template "${name}"  created.`,
            icon: 'done',
            time: 2500,
          },
          NotificationType.Success,
        );
      } catch (error) {
        if (isConflictError(error)) {
          notify(
            {
              title: 'Duplicate name',
              text: `Template with name "${rest.name}" already exists`,
              icon: 'exclamation',
              time: 7000,
            },
            NotificationType.Error,
          );
        } else {
          notify(
            {
              title: 'An error occurred',
              text: `An error occurred when trying to create the "${rest.name}" template`,
              icon: 'exclamation',
              time: 2500,
            },
            NotificationType.Error,
          );
        }
      }

      onClose();
    },
    [createTemplate, updateTemplateTranslations, notify, onClose],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHead
          title={`Add new ${TEMPLATE_NAMES[templateType].toLowerCase()}`}
          onClose={onClose}
        />
        <ModalBody className={styles.templatesModalBody}>
          <HookFormText
            name="name"
            label="Template name"
            control={control}
            rules={{
              required: 'Template name is required',
              validate: validateName,
            }}
            helpIcon={{
              title: 'Template name',
              text: 'Template name should only be in lowercase and not contain any whitespace or special characters',
            }}
            componentProps={{
              placeholder: 'Template name...',
            }}
          />

          <Box padding="400 0 0 0">
            <h5 className={styles.translationsHeader}>Template Translations</h5>
            <Box className={styles.translationsBody}>
              <label className={styles.languagesLabel}>Languages</label>
              <TabsWithContent
                tabs={tabsLabels}
                initiallySelectedTabId={defaultLanguageCode}
                tabsPlacement="left"
                tabsClassName={styles.tabs}
                tabClassName={styles.tab}
                tabSelectedClassName={styles.tabSelected}
              >
                {languages.map(({ code }) => {
                  const isDefaultLanguageCode = code === defaultLanguageCode;
                  const isEnabled = !!(
                    code === 'en' || watch(`translations.${code}.isEnabled`)
                  );

                  return (
                    <div className={styles.languageForm} key={code}>
                      <HookFormText
                        name={
                          isDefaultLanguageCode
                            ? 'subject'
                            : `translations.${code}.subject`
                        }
                        control={control}
                        label="Subject"
                        helpIcon={{
                          title: 'Subject',
                          text: `This field is used to fill out the subject line in emails when composing emails and for auto-replies. The subject line is not used if the template is used in chat or you are replying to an email. You can use these variables: ${helpText}`,
                        }}
                        componentProps={{
                          placeholder: 'Subject...',
                        }}
                      />
                      <div className={styles.templateContentContainer}>
                        <Controller
                          name={
                            isDefaultLanguageCode
                              ? 'content'
                              : `translations.${code}.content`
                          }
                          control={control}
                          rules={{
                            validate: (value, formValues) => {
                              if (code === 'en') {
                                return value?.length > 0;
                              }

                              const isEnabled =
                                formValues.translations?.[code]?.isEnabled ??
                                false;

                              if (!isEnabled) {
                                return true;
                              }

                              return value?.length > 0;
                            },
                          }}
                          render={({ field }) => (
                            <FieldTemplate
                              label="Template content"
                              input={field}
                              placeholder="Content..."
                              height="355px"
                              helpIcon={{
                                title: 'Template Content',
                                text: `The content is written in HTML and supports [Liquid](https://shopify.github.io/liquid/) too. \n\n${helpText}`,
                              }}
                              required={isEnabled}
                            />
                          )}
                        />
                      </div>
                    </div>
                  );
                })}
              </TabsWithContent>
            </Box>
          </Box>
        </ModalBody>
        <ModalFoot
          cancelOnClick={onClose}
          confirmOnClick={handleSubmit(onSubmit)}
          confirmName={`Add ${TEMPLATE_NAMES[
            templateType
          ].toLowerCase()} template`}
          confirmProps={{
            type: 'submit',
            disabled:
              !formState.isValid ||
              formState.isSubmitting ||
              !defaultLanguageContent ||
              defaultLanguageContent.length === 0,
          }}
        />
      </form>
    </Modal>
  );
};
